import { useEffect, useState } from 'react';
import { Icon } from '../../components/Icon';
import { Segmented } from '../../components/Segmented';
import { Select, type SelectOption } from '../../components/Select';
import { api, type ConnectedProvider, type ModelLimits } from '../../lib/api';
import { useStore } from '../../store/StoreProvider';
import { modelKey } from '../agents/ModelPicker';
import { BudgetBlock } from './BudgetBlock';
import { SettingsBlock } from './SettingsBlock';

// Impostazioni › Limiti dei modelli (tela, «Impostazioni», scheda omonima; spec del 2026-11-02):
// cosa fare quando un modello finisce i token o è sovraccarico. La sessione non si chiude: cambia
// modello o si mette in pausa. I valori di partenza vengono dal server e sono proposte.
const PCTS = [70, 75, 80, 85, 90, 95, 100];
const RETRIES = [5, 10, 15, 30, 60, 120];

/** Tutti i modelli dei provider collegati, raggruppati per provider. */
function modelOptions(providers: ConnectedProvider[]): SelectOption[] {
  return providers.flatMap((p) =>
    (p.catalog.length ? p.catalog : p.models.map((m) => ({ id: m, label: m }))).map((m) => ({
      value: modelKey(p, m.id),
      label: m.label,
      group: p.name,
    })),
  );
}

export function LimitsTab() {
  const { toast } = useStore();
  const [cfg, setCfg] = useState<ModelLimits | null>(null);
  const [providers, setProviders] = useState<ConnectedProvider[]>([]);
  useEffect(() => {
    api
      .modelLimits()
      .then(setCfg)
      .catch(() => setCfg(null));
    api
      .providers()
      .then((r) => setProviders(r.providers))
      .catch(() => setProviders([]));
  }, []);

  const models = modelOptions(providers);
  const name = (id: string) => models.find((m) => m.value === id)?.label ?? id;
  const known = (id: string) => models.some((m) => m.value === id);

  const save = (next: ModelLimits) => {
    setCfg(next);
    api
      .setModelLimits(next)
      .then(setCfg)
      .catch((e: Error) => toast({ kind: 'danger', title: 'Non salvato', body: e.message }));
  };
  if (!cfg) return <p className="t-small dim">Carico le impostazioni…</p>;

  const table = cfg.fallbacks;
  const setRow = (from: string, to: string[]) =>
    save({ ...cfg, fallbacks: { ...table, [from]: to } });
  const dropRow = (from: string) => {
    const rest = { ...table };
    delete rest[from];
    save({ ...cfg, fallbacks: rest });
  };
  const free = models.filter((m) => !(m.value in table));

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <SettingsBlock
        over="Limiti dei modelli"
        title="Quando un modello finisce i token o è sovraccarico"
        hint="La sessione non si chiude: cambia modello oppure si mette in pausa e riprende da sola."
        control={
          <Segmented
            label="Cosa fare"
            value={cfg.mode}
            items={[
              { value: 'switch', label: 'Passa ad altri modelli' },
              { value: 'wait', label: 'Fermati e riprendi' },
            ]}
            onChange={(mode) => save({ ...cfg, mode })}
          />
        }
      />

      <div className="limits-grid">
        <div className="settings-box limits-cell">
          <span className="t-strong">Finestra di 5 ore</span>
          <span className="t-caption dim">
            Ci si ferma prima di questa soglia e si riprende al rinnovo.
          </span>
          <Select
            label="Soglia della finestra di 5 ore"
            value={String(cfg.fiveHourPct)}
            options={PCTS.map((p) => ({ value: String(p), label: `${p}%` }))}
            onChange={(v) => save({ ...cfg, fiveHourPct: Number(v) })}
            width="100%"
          />
        </div>
        <div className="settings-box limits-cell">
          <span className="t-strong">Limite settimanale</span>
          <span className="t-caption dim">
            Oltre questa soglia ci si ferma sempre e ti si chiede cosa fare.
          </span>
          <Select
            label="Soglia del limite settimanale"
            value={String(cfg.weeklyPct)}
            options={PCTS.map((p) => ({ value: String(p), label: `${p}%` }))}
            onChange={(v) => save({ ...cfg, weeklyPct: Number(v) })}
            width="100%"
          />
        </div>
        <div className="settings-box limits-cell">
          <span className="t-strong">Modello sovraccarico</span>
          <span className="t-caption dim">
            {cfg.mode === 'switch'
              ? 'Si passa subito alla prima alternativa libera; senza, si riprova dopo:'
              : 'Si aspetta e si riprova, senza cambiare modello.'}
          </span>
          <Select
            label="Dopo quanto riprovare"
            value={String(cfg.busyRetryMinutes)}
            options={RETRIES.map((m) => ({
              value: String(m),
              label:
                m < 60
                  ? `Riprova dopo ${m} minuti`
                  : `Riprova dopo ${m / 60} ${m === 60 ? 'ora' : 'ore'}`,
            }))}
            onChange={(v) => save({ ...cfg, busyRetryMinutes: Number(v) })}
            width="100%"
          />
        </div>
      </div>

      <div className="limits-note">
        <Icon name="info" size={18} />
        <span className="t-small">
          Solo Claude Code dice quanto è piena la finestra e quando si rinnova, quindi solo lì ci si
          ferma <strong>prima</strong>. Con Codex e Antigravity si reagisce all’errore: si cambia
          modello oppure si aspetta e si riprova.
        </span>
      </div>

      <SettingsBlock
        over="Tabella di cambio"
        title="Le alternative di ogni modello, in ordine"
        hint="Si prova la prima libera. Se nessuna lo è, sceglie il PM fra i modelli collegati e lo scrive in chat."
        control={
          free.length > 0 ? (
            <div style={{ width: 280, maxWidth: '100%' }}>
              <Select
                label="Aggiungi un modello alla tabella"
                value=""
                options={[{ value: '', label: 'Aggiungi un modello…' }, ...free]}
                onChange={(v) => v && setRow(v, [])}
                width="100%"
              />
            </div>
          ) : undefined
        }
      >
        <div style={{ display: 'grid', gap: 10 }}>
          {Object.keys(table).length === 0 && (
            <p className="t-small dim" style={{ margin: 0 }}>
              Nessun modello in tabella: quando serve, sceglie il PM.
            </p>
          )}
          {Object.entries(table).map(([from, to]) => {
            const left = models.filter((m) => m.value !== from && !to.includes(m.value));
            return (
              <div key={from} className="limits-row">
                <div className="limits-from">
                  <span className="t-strong">{name(from)}</span>
                  <span className="t-caption dim">{known(from) ? from : 'Non più collegato'}</span>
                </div>
                <span className="limits-arrow" aria-hidden="true">
                  <Icon name="arrow-right" size={18} />
                </span>
                <div className="limits-to">
                  {to.map((m, i) => (
                    <span key={m} className="limits-chip" data-lost={known(m) ? undefined : 'true'}>
                      <span className="limits-n">{i + 1}</span>
                      {known(m) ? name(m) : `${m} · non più collegato`}
                      <button
                        type="button"
                        aria-label={`Togli ${name(m)}`}
                        onClick={() =>
                          setRow(
                            from,
                            to.filter((x) => x !== m),
                          )
                        }
                      >
                        <Icon name="x" size={12} />
                      </button>
                    </span>
                  ))}
                  {left.length > 0 && (
                    <div style={{ width: 220, maxWidth: '100%' }}>
                      <Select
                        label={`Aggiungi un'alternativa a ${name(from)}`}
                        value=""
                        options={[{ value: '', label: '+ alternativa' }, ...left]}
                        onChange={(v) => v && setRow(from, [...to, v])}
                        width="100%"
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="limits-drop"
                  aria-label={`Togli ${name(from)} dalla tabella`}
                  onClick={() => dropRow(from)}
                >
                  <Icon name="trash" size={16} />
                </button>
              </div>
            );
          })}
        </div>
      </SettingsBlock>
      <BudgetBlock />
    </div>
  );
}
